// src/components/LiveAttendancePanel.jsx
// 出席受付ページの上に出す「リアルタイム出席」【2026-11-02】
//
// もとは /live の独立ページ（pages/LiveAttendance.jsx）で、授業を選ぶと 5 秒ごとに読み直していた。
// 受付を始めてから見に行くページなのに、毎回ページを移って授業を選び直す必要があったので、
// 出席受付のページにまとめ、受付中の回があるときだけ一番上に出す。
//   ・受付中の回（ビーコン）／有効なコード（OTP）ごとに 1 枚
//   ・見出しを押すとたためる。たたんだ状態は回ごとに覚えておく（localStorage）
//   ・開いている間だけ 5 秒ごとに読み直す（たたんでいる間は見出しの人数だけ。出席受付のカードが 10 秒ごとに拾う）
// 使う API はもとのページと同じ（/api/attendance/class/:id と /api/attendance/live/:id、?sessionId= で回を指定）。

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { StatusBadge as RosterBadge } from './StudentStatusBadges';
// 同じ端末からの出席（誰の端末で誰の分が打刻されたか）【2026-11-02】
import DeviceSharingList from './DeviceSharingList';
import { confirmDialog } from './ConfirmDialog';

const LS_COLLAPSED = 'liveCollapsed';

const loadCollapsed = () => {
  try { return JSON.parse(localStorage.getItem(LS_COLLAPSED) || '{}') || {}; }
  catch (e) { return {}; }
};
const saveCollapsed = (key, v) => {
  try {
    const all = loadCollapsed();
    if (v) all[key] = true; else delete all[key];
    // 終わった回のぶんが溜まらないように、新しい 30 件だけ残す
    const keys = Object.keys(all);
    if (keys.length > 30) keys.slice(0, keys.length - 30).forEach(k => delete all[k]);
    localStorage.setItem(LS_COLLAPSED, JSON.stringify(all));
  } catch (e) { /* 保存できなくても動く */ }
};

/**
 * 受付中の回の一覧から、出すパネルを作る。
 *   sessions … AttendanceStartCard の openSessions（{ sessionId, classId, className, present, late }）
 *   otps     … 有効なコード（{ code, classId, className, expiresAt, usedCount }）
 * 同じ授業でビーコンの回とコードが両方あるときは、回のほうに 1 枚にまとめる。
 */
export function buildLiveItems(sessions = [], otps = []) {
  const items = [];
  const seen = new Set();
  for (const s of sessions) {
    const otp = otps.find(o => String(o.classId) === String(s.classId)) || null;
    items.push({ key: 's' + s.sessionId, classId: s.classId, sessionId: s.sessionId,
                 className: s.className, present: s.present, late: s.late, otp });
    seen.add(String(s.classId));
  }
  for (const o of otps) {
    if (seen.has(String(o.classId))) continue;
    seen.add(String(o.classId));
    items.push({ key: 'o' + o.classId, classId: o.classId, sessionId: null,
                 className: o.className, present: null, late: null, otp: o });
  }
  return items;
}

export default function LiveAttendancePanel({ item }) {
  const [collapsed, setCollapsed] = useState(() => !!loadCollapsed()[item.key]);
  const [status, setStatus] = useState(null);
  const [live, setLive]     = useState([]);
  const [err, setErr]       = useState('');
  const [updatedAt, setUpdatedAt] = useState(null);
  const [shared, setShared] = useState([]);   // 同じ端末からの出席
  const alive = useRef(true);
  useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);

  const auth = () => ({ Authorization: 'Bearer ' + localStorage.getItem('token') });
  const q = item.sessionId ? '?sessionId=' + item.sessionId : '';

  const fetchData = useCallback(async () => {
    try {
      const [sR, lR, dR] = await Promise.all([
        fetch('/api/attendance/class/' + item.classId + q, { headers: auth() }),
        fetch('/api/attendance/live/'  + item.classId + q, { headers: auth() }),
        // 回が分からない（OTP だけの）ときは今日の分
        fetch('/api/attendance/device-sharing/' + item.classId + (q || '?today=1'), { headers: auth() }).catch(() => null),
      ]);
      const s = await sR.json();
      const l = await lR.json();
      const d = dR ? await dR.json().catch(() => null) : null;
      if (!alive.current) return;
      if (d && d.success) setShared(d.groups || []);
      if (s.success) setStatus(s);
      if (l.success) setLive(l.attendances || []);
      if (!s.success && s.message) setErr(s.message); else setErr('');
      setUpdatedAt(new Date());
    } catch (e) { if (alive.current) setErr(e.message); }
  }, [item.classId, q]); // eslint-disable-line react-hooks/exhaustive-deps -- auth は毎回作るが中身は同じ

  // 開いている間だけ 5 秒ごと（画面が裏に回っている間は止める）
  useEffect(() => {
    if (collapsed) return undefined;
    fetchData();
    const t = setInterval(() => {
      if (typeof document !== 'undefined' && document.hidden) return;
      fetchData();
    }, 5000);
    return () => clearInterval(t);
  }, [collapsed, fetchData]);

  const toggle = () => {
    setCollapsed(c => { saveCollapsed(item.key, !c); return !c; });
  };

  // 名簿外の打刻【2026-09-14】
  //   ビーコンは教員ごとなので、教室に居合わせただけの他クラスの学生でも打刻が通る。
  //   サーバーは記録だけして enrolled=false で返してくるので、ここで拾って見せる。
  const unenrolled = live.filter(r => r.enrolled === false);

  const addToRoster = async (row) => {
    if (!(await confirmDialog(`${row.name}（${row.student_id}）を履修者に追加しますか？`))) return;
    setErr('');
    try {
      const res = await fetch('/api/schedule/classes/' + item.classId + '/enrollments', {
        method: 'POST',
        headers: { ...auth(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ studentId: row.student_id }),
      });
      const d = await res.json();
      if (!d.success) throw new Error(d.message || '追加に失敗しました');
      await fetchData();
    } catch (e) { setErr(e.message); }
  };

  // 見出しの人数。開いていて読めていればその数、たたんでいれば受付の一覧の数
  const present = status ? status.present : item.present;
  const late    = status ? status.late    : item.late;
  const total   = status ? status.total   : null;
  const notYet  = status ? (status.students || []).filter(s => !s.has_attended).length : null;

  return (
    <section className={'live-panel' + (collapsed ? ' is-collapsed' : '')} aria-label={'リアルタイム出席：' + item.className}>
      <button type="button" className="live-panel-head" onClick={toggle} aria-expanded={!collapsed}>
        <span className="live-dot" aria-hidden="true" />
        <span className="live-panel-tag">受付中</span>
        <span className="live-panel-name">{item.className}</span>
        {item.otp && <span className="live-panel-otp" title="有効な OTP コード">OTP {item.otp.code}</span>}
        {shared.length > 0 && <span className="live-panel-dsh" title="同じ端末から複数の人が打刻しています">⚠ 同じ端末 {shared.length}</span>}
        <span className="live-panel-counts">
          {present != null && <>出席 <b>{present}</b></>}
          {late != null && <>　遅刻 <b>{late}</b></>}
          {total != null && <>　／ 履修 {total}</>}
          {present == null && item.otp && <>打刻 <b>{item.otp.usedCount || 0}</b></>}
        </span>
        <span className="live-panel-toggle">{collapsed ? '開く ▼' : 'たたむ ▲'}</span>
      </button>

      {!collapsed && (
        <div className="live-panel-body">
          {err && <p className="live-panel-err">{err}</p>}
          {!status && !err && <p className="live-panel-muted">読み込み中…</p>}

          {status && (
            <>
              <div className="live-stats">
                <SmallStat label="履修者"  value={status.total}   color="#3b82f6" />
                <SmallStat label="出席"    value={status.present} color="#22c55e" />
                <SmallStat label="遅刻"    value={status.late}    color="#f59e0b" />
                <SmallStat label="未打刻"  value={notYet}         color="#6b7280" />
                <SmallStat label="出席率"  value={status.attendanceRate != null ? status.attendanceRate + '%' : '—'} color="#0ea5e9" />
              </div>

              {shared.length > 0 && (
                <div className="live-dsh">
                  <h3>同じ端末からの出席（{shared.length}件）</h3>
                  <DeviceSharingList groups={shared} compact />
                  <p className="live-panel-muted" style={{ marginBottom: 0 }}>
                    友だちに打刻してもらうことは認めていません。代わりに打刻された学生には、アプリで「どうしても必要だった場合は先生に伝えてください」と知らせています（出席の記録は残っています）。
                    持ち主は、その端末でいちばん多く打刻している人です。あとから出席集計の「同じ端末」タブでも見られます。
                  </p>
                </div>
              )}

              {unenrolled.length > 0 && (
                <div className="live-unenrolled">
                  <h3>履修者名簿に無い打刻（{unenrolled.length}件）</h3>
                  <p className="live-panel-muted">
                    打刻は記録されていますが、出席率の分母には入っていません。
                    履修しているはずの学生なら「履修者に追加」を押してください。
                    教室に居合わせただけの学生なら、そのままで構いません。
                  </p>
                  <table className="data-table">
                    <thead><tr><th>時刻</th><th>学籍番号</th><th>氏名</th><th></th></tr></thead>
                    <tbody>
                      {unenrolled.map((r, i) => (
                        <tr key={i}>
                          <td>{formatTime(r.timestamp)}</td>
                          <td>{r.student_id}</td>
                          <td>{r.name}</td>
                          <td><button className="btn-secondary" onClick={() => addToRoster(r)}>履修者に追加</button></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              <div className="live-columns">
                <div className="live-col">
                  <h3>打刻の流れ（新しい順・{live.length}件）</h3>
                  <div className="live-scroll">
                    {live.length === 0 ? (
                      <p className="live-panel-muted" style={{ textAlign: 'center' }}>まだ打刻はありません</p>
                    ) : (
                      <table className="data-table">
                        <thead><tr><th>時刻</th><th>学籍番号</th><th>氏名</th><th>状態</th></tr></thead>
                        <tbody>
                          {live.map((r, i) => (
                            <tr key={i}>
                              <td>{formatTime(r.timestamp)}</td>
                              <td>{r.student_id}</td>
                              <td>{r.name}{r.enrolled === false && <RosterBadge kind="notOnRoster" />}</td>
                              <td><StatusBadge status={r.status} /></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                </div>
                <div className="live-col">
                  <h3>履修者（未打刻を上に）</h3>
                  <div className="live-scroll">
                    <table className="data-table">
                      <thead><tr><th>学籍番号</th><th>氏名</th><th>状態</th><th>時刻</th></tr></thead>
                      <tbody>
                        {sortRoster(status.students).map(s => (
                          <tr key={s.id}>
                            <td>{s.student_id}</td>
                            <td><strong>{s.name}</strong></td>
                            <td>{s.has_attended ? <StatusBadge status={s.status} /> : <span style={{ color: '#888' }}>未打刻</span>}</td>
                            <td>{formatTime(s.timestamp)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
              <p className="live-panel-foot">
                5秒ごとに更新{updatedAt ? '（最終 ' + updatedAt.toLocaleTimeString('ja-JP') + '）' : ''}。たたむと更新を止めます。
              </p>
            </>
          )}
        </div>
      )}
    </section>
  );
}

// 未打刻を上に。あとはサーバーの並び順のまま（安定ソート）
function sortRoster(students) {
  return (students || []).map((s, i) => [s, i])
    .sort((a, b) => (Number(!!a[0].has_attended) - Number(!!b[0].has_attended)) || (a[1] - b[1]))
    .map(x => x[0]);
}

function SmallStat({ label, value, color }) {
  return (
    <div className="live-stat" style={{ borderTopColor: color }}>
      <div className="live-stat-label">{label}</div>
      <div className="live-stat-value" style={{ color }}>{value == null ? '—' : value}</div>
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    present: { text: '出席', bg: '#dcfce7', fg: '#166534' },
    late:    { text: '遅刻', bg: '#fef3c7', fg: '#92400e' },
    absent:  { text: '欠席', bg: '#fee2e2', fg: '#991b1b' },
    // 時間外・名簿外など、記録はしたが確認が要るもの【2026-09-14】
    irregular: { text: '要確認', bg: '#ffedd5', fg: '#9a3412' },
    early_leave: { text: '早退', bg: '#e0e7ff', fg: '#3730a3' },
    excused: { text: '公欠', bg: '#ede9fe', fg: '#5b21b6' },
  };
  const s = map[status] || { text: status, bg: '#e5e7eb', fg: '#374151' };
  return (
    <span style={{
      display: 'inline-block', padding: '0.2rem 0.7rem', borderRadius: '12px',
      background: s.bg, color: s.fg, fontSize: '0.8rem', fontWeight: 600,
    }}>{s.text}</span>
  );
}

function formatTime(ts) {
  if (!ts) return '-';
  try {
    const d = new Date(ts);
    if (isNaN(d.getTime())) return '-';
    return d.toLocaleTimeString('ja-JP', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  } catch (e) { return '-'; }
}
